import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";

// Public explorer
import Dashboard from "@/pages/Dashboard";
import Blocks from "@/pages/Blocks";
import BlockDetail from "@/pages/BlockDetail";
import TransactionDetail from "@/pages/TransactionDetail";
import WalletDetail from "@/pages/WalletDetail";
import Dev from "@/pages/Dev";

// Auth
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";

// App (authenticated)
import AppDashboard from "@/pages/app/AppDashboard";
import AppWallets from "@/pages/app/AppWallets";
import SendPage from "@/pages/app/Send";
import Transactions from "@/pages/app/Transactions";
import Settings from "@/pages/app/Settings";
import FX from "@/pages/app/FX";
import Holds from "@/pages/app/Holds";
import Scheduled from "@/pages/app/Scheduled";
import AppTxDetail from "@/pages/app/AppTxDetail";
import Contracts from "@/pages/app/Contracts";
import ContractDetail from "@/pages/app/ContractDetail";
import ContractNew from "@/pages/app/ContractNew";
import Invoices from "@/pages/app/Invoices";
import InvoiceDetail from "@/pages/app/InvoiceDetail";
import AdminBranding from "@/pages/app/AdminBranding";
import AdminBusinesses from "@/pages/app/AdminBusinesses";
import AdminFX from "@/pages/app/AdminFX";
import AdminRisk from "@/pages/app/AdminRisk";
import AdminCompliance from "@/pages/app/AdminCompliance";
import Developer from "@/pages/app/Developer";
import DeveloperAgentDetail from "@/pages/app/DeveloperAgentDetail";
import AgentApprovals from "@/pages/app/AgentApprovals";
import DemoAgents from "@/pages/app/DemoAgents";
import AdminValidators from "@/pages/app/AdminValidators";
import AdminNetworkHealth from "@/pages/app/AdminNetworkHealth";
import AdminWebhooks from "@/pages/app/AdminWebhooks";
import Webhooks from "@/pages/app/Webhooks";
import WebhookDetail from "@/pages/app/WebhookDetail";
import OdooIntegration from "@/pages/app/OdooIntegration";
import ApiKeys from "@/pages/app/ApiKeys";
import Validators from "@/pages/Validators";
import ValidatorDetail from "@/pages/ValidatorDetail";
import PublicAgent from "@/pages/PublicAgent";
import PublicEmbedPay from "@/pages/PublicEmbedPay";

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <AuthProvider>
            <Routes>
              {/* Auth-only standalone routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Public explorer */}
              <Route element={<Layout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/blocks" element={<Blocks />} />
                <Route path="/blocks/:n" element={<BlockDetail />} />
                <Route path="/tx/:id" element={<TransactionDetail />} />
                <Route path="/wallets/:address" element={<WalletDetail />} />
                <Route path="/validators" element={<Validators />} />
                <Route path="/validators/:id" element={<ValidatorDetail />} />
                <Route path="/agents/:did" element={<PublicAgent />} />
                <Route path="/x402/:token" element={<PublicEmbedPay />} />
                <Route path="/dev" element={<Dev />} />
              </Route>

              {/* Authenticated app shell */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<AppDashboard />} />
                <Route path="wallets" element={<AppWallets />} />
                <Route path="send" element={<SendPage />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="settings" element={<Settings />} />
                <Route path="fx" element={<FX />} />
                <Route path="holds" element={<Holds />} />
                <Route path="scheduled" element={<Scheduled />} />
                <Route path="tx/:id" element={<AppTxDetail />} />
                <Route path="contracts" element={<Contracts />} />
                <Route path="contracts/new" element={<ContractNew />} />
                <Route path="contracts/:id" element={<ContractDetail />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="developer" element={<Developer />} />
                <Route path="developer/:id" element={<DeveloperAgentDetail />} />
                <Route path="agent-approvals" element={<AgentApprovals />} />
                <Route path="demo/agents" element={<DemoAgents />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="webhooks/:id" element={<WebhookDetail />} />
                <Route path="integrations/odoo" element={<OdooIntegration />} />
                <Route path="settings/api-keys" element={<ApiKeys />} />
                <Route
                  path="admin/branding"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminBranding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/businesses"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminBusinesses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/fx"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminFX />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/risk"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminRisk />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/compliance"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminCompliance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/validators"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminValidators />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/network-health"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminNetworkHealth />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/webhooks"
                  element={
                    <ProtectedRoute roles={["super_admin"]}>
                      <AdminWebhooks />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
    </div>
  );
}
